/**
 * The live voice translator's decisions (SEO_BRIEF_10 section 2), with no DOM of its own: it
 * draws through a `LiveView` (mount.ts on the page, a record in web/test/live-translator.test.ts)
 * and reaches the browser through the pieces it is handed, so every turn of it is tested with
 * stand-ins.
 *
 * TWO MODES, ONE SESSION. Speak: one person, side `a` speaks in `pair.a` and hears `pair.b`.
 * Conversation: two halves, `a` (the bottom one, nearest the phone's owner) and `b` (the top
 * one, which Face to face turns round for the person across the table). Each side has its own
 * microphone and language; a side's words are translated into the other side's language and
 * read aloud there. Only one microphone is on at a time: a tap on the other side's switches who
 * is talking, a tap on the side that is on stops it. The session's lines are one list, drawn by
 * whichever mode is open, kept across a switch of mode and emptied by Clear.
 *
 * A SENTENCE. The browser's recognition (lib/live-speech.ts, one per side, so words still
 * settling after a switch stay with the side that said them) hands over a finished stretch of
 * speech. It is cut into pieces of at most 300 characters (sentences.ts), each shown at once
 * with its translation pending, and translated in order: on the device when Chrome's built-in
 * translator has the pair (device.ts), else by `/api/live-translate` (lib/gemini.ts
 * `liveTranslate`), asked once more when the server is busy and then left in the original with
 * a short note. Then, with Read aloud on, it is read in the target language (speech-out.ts),
 * with the microphone held meanwhile so it does not hear the phone and take the reading for the
 * speaker. Recognition that stops in a silence starts again by itself (lib/live-speech.ts, as
 * Brief 08's live mode does), and the screen is kept on while it listens.
 *
 * HOLD TO TALK (section 2.4). A browser without speech recognition (Firefox), one whose
 * recognition fails before it has given a word back because it can't reach its service or isn't
 * allowed one (Brave, Opera), and any browser after the language model's limit when the
 * translation is into English: the microphone button is held while the visitor talks
 * (lib/recorder.ts), and on release the clip goes through the site's upload and job (hold.ts
 * `sendClip`) and comes back as the translation.
 *
 * THE LIMIT (the founder's ruling 3, limit.ts). When the model can't take a sentence, the
 * microphone stops, and the page says Brief 09's sentence and, into English, offers Hold to
 * talk. A sentence the device translates is never refused.
 *
 * THE USAGE EVENTS (section 2.6) go through `count`: a session's first sentence in each mode
 * (`start`), each sentence translated and by what (`sentence`), the limit met (`limit`, once
 * until it lifts), and the session saved as text (`download`).
 */

import { ApiError } from '../api.ts';
import type { Problem } from '../api-types.ts';
import { type GeminiLimit, GeminiLimitError } from '../gemini.ts';
import { createLive, type Live, type LiveFailure, type RecognitionCtor } from '../live-speech.ts';
import {
  createRecorder,
  type Recorder,
  type RecorderEnv,
  type RecorderFailure,
  type Recording,
  type WakeLockLike,
} from '../recorder.ts';
import type { LiveTranslateEngine, LiveTranslateMode } from '../usage.ts';
import type { DeviceTranslators } from './device.ts';
import { type LanguagePair, liveLanguage, recognitionLocale } from './languages.ts';
import { limitNotice, spentForToday, stillLimited } from './limit.ts';
import { splitSentences } from './sentences.ts';
import type { SpeechOut } from './speech-out.ts';
import { type Line, type Side, sessionFileName, sessionText } from './transcript.ts';

export type { Line, Side } from './transcript.ts';
export type Mode = LiveTranslateMode;
export type Engine = LiveTranslateEngine;

/** The status line's keys: the template's `[data-lt-label]` words. */
export type StatusKey =
  | 'listening'
  | 'stopped'
  | 'speaking'
  | 'network'
  | 'language'
  | 'asking'
  | 'holding'
  | 'hold-short'
  | 'hold-again'
  | 'translating'
  | 'copied'
  | 'copy-failed'
  | RecorderFailure;

const LIVE_FAILURE_STATUS: Record<LiveFailure, StatusKey> = {
  blocked: 'blocked',
  'no-mic': 'no-mic',
  network: 'network',
  language: 'language',
  failed: 'stopped',
};

/** A clip shorter than this is a tap, not a sentence: dropped, with a line saying hold longer. */
export const MIN_HOLD_S = 0.6;
/** A held clip stops by itself here: a sentence or two, never a monologue. */
export const HOLD_CAP_S = 60;
/** A busy server's answer is asked again after its Retry-After, but never later than this. */
export const RETRY_WAIT_CAP_MS = 2000;

/* ── the turn's rules ──────────────────────────────────────────────────── */

export const other = (side: Side): Side => (side === 'a' ? 'b' : 'a');

/** The sides that have a microphone in `mode`. */
export function sidesOf(mode: Mode): readonly Side[] {
  return mode === 'speak' ? ['a'] : ['a', 'b'];
}

/** The languages a side speaks and is translated into. */
export function directionOf(side: Side, pair: LanguagePair): { from: string; to: string } {
  return side === 'a' ? { from: pair.a, to: pair.b } : { from: pair.b, to: pair.a };
}

/**
 * The side listening after a tap on `pressed`'s microphone, when `on` is listening now: the
 * side that is on stops (null), any other tap gives that side the turn.
 */
export function turnAfter(on: Side | null, pressed: Side): Side | null {
  return on === pressed ? null : pressed;
}

/**
 * The pair after `side` picks `code`: picking the other side's language swaps the two (no
 * pair is one language twice), an unknown code changes nothing.
 */
export function pairAfterPick(pair: LanguagePair, side: Side, code: string): LanguagePair {
  if (!liveLanguage(code) || pair[side] === code) return pair;
  if (pair[other(side)] === code) return { a: pair.b, b: pair.a };
  return side === 'a' ? { a: code, b: pair.b } : { a: pair.a, b: code };
}

/* ── what it draws with, and what it is handed ─────────────────────────── */

export interface LiveView {
  /** The mode on screen, and the session's lines drawn afresh in it. */
  mode(mode: Mode, lines: readonly Line[]): void;
  /** The two pickers' languages, in both modes. */
  languages(pair: LanguagePair): void;
  /** The side whose microphone is on, or none. */
  listening(side: Side | null): void;
  /** The microphone buttons are held to talk (true) or tapped (false). */
  holdMode(on: boolean): void;
  /** The side whose button is held now, or none. */
  holding(side: Side | null): void;
  /** The words still being guessed on `side`, '' for none. */
  interim(side: Side, text: string): void;
  /** A line added, or changed (its translation arrived, or failed). */
  line(line: Line): void;
  /** A line taken away again (a clip the server refused: the banner says why). */
  drop(line: Line): void;
  status(key: StatusKey | null): void;
  /** No voice reads `code` in this browser: said once per language. */
  noVoice(code: string): void;
  /** The limit's sentence and Hold to talk offer, or null to take it away. */
  limit(message: string | null, offerHold: boolean): void;
  /** The line saying the screen may dim: while listening without a wake lock. */
  screenLine(show: boolean): void;
  /** Whether there is a session to copy, download or clear. */
  hasText(any: boolean): void;
  readAloud(on: boolean): void;
  faceToFace(on: boolean): void;
  /** Neither speech recognition nor recording in this browser: one line instead. */
  unsupported(): void;
  /** The session couldn't be made (E14, lib/api.ts): the page's banner says so. */
  noSession(problem: Problem): void;
}

/** A recording to translate, as the visitor held it. */
export interface HoldClip {
  readonly recording: Recording;
  readonly from: string;
  readonly to: string;
}

/** What came back for a clip. `ok: false`: refused, and the page's banner says why. */
export type HoldAnswer =
  | {
      readonly ok: true;
      /** The words in `to`, or in `from` when the translation was unavailable. */
      readonly text: string;
      /** The words as said, when known (a translation not into English keeps them). */
      readonly original: string | null;
      readonly engine: 'whisper' | 'gemini';
      /** The model had no room for it: `text` is the original. */
      readonly unavailable: boolean;
    }
  | { readonly ok: false };

export interface UsageHooks {
  start(mode: Mode, source: string, target: string): void;
  sentence(engine: Engine, source: string, target: string): void;
  limit(scope: GeminiLimit['scope']): void;
  download(): void;
}

export interface LiveTranslatorDeps {
  view: LiveView;
  /** The browser's speech recognition; null where it has none (Firefox). */
  recognition: RecognitionCtor | null;
  /** `navigator.languages`, for the recognition's regional locale. */
  browserLanguages?: readonly string[];
  /** Chrome's built-in translator; null where there is none (every phone). */
  device: DeviceTranslators | null;
  /** `/api/live-translate`: the translation, or a `GeminiLimitError` / `ApiError`. */
  server(text: string, from: string, to: string): Promise<string>;
  /** Read aloud; null where the browser has no `speechSynthesis`. */
  speech: SpeechOut | null;
  /** The microphone for Hold to talk; null where the browser can't record. */
  recorderEnv: RecorderEnv | null;
  /** A held clip to the site's upload and job, and back. */
  sendClip(clip: HoldClip): Promise<HoldAnswer>;
  /** `navigator.wakeLock.request('screen')`, where the browser has it. */
  requestWakeLock?: (() => Promise<WakeLockLike>) | undefined;
  /** Calls `fn` whenever the page becomes visible again; returns the unsubscribe. */
  onVisible?: ((fn: () => void) => () => void) | undefined;
  count: UsageHooks;
  /** Save a file from this tab. */
  save(blob: Blob, filename: string): void;
  copy(text: string): Promise<boolean>;
  /** A language's name as the session's text names it. */
  nameOf(code: string): string;
  /** Mint the session ahead of the first sentence the server will translate. */
  warm?(): void;
  /** The recognition's clock (live-speech's quick-restart check). */
  now?: () => number;
  /** The wall clock, for the limit's end and the download's name. */
  clock?: () => Date;
  sleep?: (ms: number) => Promise<void>;
  initial: { mode: Mode; pair: LanguagePair; readAloud: boolean };
}

export interface LiveTranslator {
  readonly mode: Mode;
  readonly pair: LanguagePair;
  /** The side listening (or held for reading aloud), as the visitor switched it. */
  readonly on: Side | null;
  readonly holdMode: boolean;
  readonly readAloud: boolean;
  readonly lines: readonly Line[];
  /** Every translation and reading asked for so far has finished. For the tests. */
  idle(): Promise<void>;
  setMode(mode: Mode): void;
  setLanguage(side: Side, code: string): void;
  swap(): void;
  /** A tap on a side's microphone. */
  press(side: Side): void;
  /** A side's button pressed and released, in Hold to talk. */
  holdStart(side: Side): void;
  holdEnd(side: Side): void;
  /** The limit's offer taken: the buttons become Hold to talk. */
  useHold(): void;
  setReadAloud(on: boolean): void;
  setFaceToFace(on: boolean): void;
  copy(): Promise<boolean>;
  download(): boolean;
  clear(): void;
}

export function createLiveTranslator(deps: LiveTranslatorDeps): LiveTranslator {
  const { view } = deps;
  const clock = deps.clock ?? (() => new Date());
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const browser = deps.browserLanguages ?? [];

  let mode: Mode = deps.initial.mode;
  let pair: LanguagePair = deps.initial.pair;
  let readAloud = deps.initial.readAloud && deps.speech !== null;
  let facing = false;
  const lines: Line[] = [];
  let nextId = 1;
  /** Bumped by Clear, so a translation still on its way to a cleared line paints nothing. */
  let generation = 0;
  /** The modes this session has had a sentence in: `start` is counted once for each. */
  const started = new Set<Mode>();
  const toldNoVoice = new Set<string>();

  /** The side the visitor has switched on (it may be held while a translation is read). */
  let wanted: Side | null = null;
  /** A stop made here (a switch, a reading, a new language) that the view must not show. */
  let quiet = false;
  /** The microphone is held for a reading, and starts again once the readings end. */
  let hushed = false;
  let readings = 0;
  let translations: Promise<void> = Promise.resolve();
  let speeches: Promise<void> = Promise.resolve();

  let limited: GeminiLimit | null = null;
  /** The target the limit was met on, for its Hold to talk offer. */
  let limitedTarget = 'en';
  /**
   * No session could be made (E14): the banner says so, and the lines already heard fail at
   * once rather than each wait for a mint of its own. A tap on a microphone tries again.
   */
  let sessionRefused = false;

  let wake: WakeLockLike | null = null;
  let unwatch: (() => void) | null = null;

  const recorder: Recorder | null = deps.recorderEnv
    ? createRecorder(deps.recorderEnv, {
        onState: (state) => onRecorderState(state),
        onTick: (seconds) => {
          if (seconds >= HOLD_CAP_S && held) holdEnd(held);
        },
        onDone: (recording) => clip(recording),
        onFail: (failure) => {
          held = null;
          clipSide = null;
          view.holding(null);
          view.status(failure);
        },
      })
    : null;
  let holdMode = deps.recognition === null && recorder !== null;
  /**
   * The recognition has given words back in this visit. Until it has, a failure that says it
   * can't reach its service, or isn't allowed to, means it never will here (Brave and Opera
   * have the constructor and no service): the buttons become Hold to talk (`liveFor`).
   */
  let recognised = false;
  /** The side whose button is down now. */
  let held: Side | null = null;
  /** The side the recording in progress belongs to. */
  let clipSide: Side | null = null;

  /* ── the screen ──────────────────────────────────────────────────── */

  async function holdScreen(): Promise<void> {
    if (!deps.requestWakeLock) {
      view.screenLine(true);
      return;
    }
    if (wake) return;
    try {
      const sentinel = await deps.requestWakeLock();
      if (wanted === null || wake) {
        void sentinel.release().catch(() => undefined);
        return;
      }
      wake = sentinel;
      sentinel.addEventListener?.('release', () => {
        if (wake === sentinel) wake = null;
      });
      view.screenLine(false);
    } catch {
      view.screenLine(true);
    }
  }

  function letScreenGo(): void {
    unwatch?.();
    unwatch = null;
    const was = wake;
    wake = null;
    if (was) void was.release().catch(() => undefined);
    view.screenLine(false);
  }

  function keepScreen(): void {
    if (!unwatch && deps.onVisible) {
      unwatch = deps.onVisible(() => {
        if (wanted !== null) void holdScreen();
      });
    }
    void holdScreen();
  }

  /* ── listening ───────────────────────────────────────────────────── */

  function quietly(fn: () => void): void {
    quiet = true;
    try {
      fn();
    } finally {
      quiet = false;
    }
  }

  const lives: Record<Side, Live> | null = deps.recognition
    ? { a: liveFor('a', deps.recognition), b: liveFor('b', deps.recognition) }
    : null;

  function liveFor(side: Side, Ctor: RecognitionCtor): Live {
    return createLive(
      Ctor,
      {
        onInterim: (words) => {
          if (words) recognised = true;
          view.interim(side, words);
        },
        onFinal: (words) => {
          if (words.trim()) recognised = true;
          heard(side, words);
        },
        onSwitch: (on) => {
          if (quiet) return;
          if (on) {
            view.listening(side);
            view.status('listening');
            keepScreen();
            return;
          }
          // Stopped by the visitor, or by a failure whose own line follows (onFail).
          if (wanted === side) wanted = null;
          if (wanted === null) {
            view.listening(null);
            view.status('stopped');
            letScreenGo();
          }
        },
        onFail: (failure) => {
          // Never heard a word, and it can't reach its service or isn't allowed one: it won't
          // here, and the microphone may still record. A microphone that is really blocked
          // says so again on the first hold.
          if (!recognised && recorder && (failure === 'network' || failure === 'blocked')) {
            toHold();
            view.status('hold-again');
            return;
          }
          view.status(LIVE_FAILURE_STATUS[failure]);
        },
      },
      deps.now,
    );
  }

  function startSide(side: Side): void {
    lives?.[side].start(recognitionLocale(pair[side], browser));
  }

  /**
   * Start `side` without the view hearing of it, as the turn passes or a reading ends: the
   * button stays pressed and the screen stays on. A start the browser refuses there and then
   * is shown as a stop (its reason is already on the status line).
   */
  function quietStart(side: Side): void {
    quietly(() => startSide(side));
    if (lives?.[side].on) {
      view.listening(side);
      return;
    }
    if (wanted === side) wanted = null;
    view.listening(null);
    letScreenGo();
  }

  function listen(side: Side): void {
    const was = wanted;
    wanted = side;
    if (was && was !== side) quietly(() => lives?.[was].stop());
    if (hushed) {
      // A reading is on: this side starts once it ends.
      view.listening(side);
      return;
    }
    if (was && was !== side) {
      // The turn passes: the screen stays on, the status stays "listening".
      quietStart(side);
      return;
    }
    startSide(side);
  }

  function stopListening(): void {
    const was = wanted;
    wanted = null;
    if (was === null) return;
    if (hushed || !lives?.[was].on) {
      view.listening(null);
      view.status('stopped');
      letScreenGo();
      return;
    }
    lives[was].stop();
  }

  /** The listening side, started again in its language (a new pick, a swap). */
  function relisten(): void {
    const side = wanted;
    if (side === null || hushed || !lives?.[side].on) return;
    quietly(() => lives[side].stop());
    quietStart(side);
  }

  /* ── reading aloud ───────────────────────────────────────────────── */

  function hush(): void {
    if (hushed || wanted === null || !lives?.[wanted].on) return;
    hushed = true;
    const side = wanted;
    quietly(() => lives[side].stop());
    view.status('speaking');
  }

  function unhush(): void {
    if (!hushed) return;
    hushed = false;
    const side = wanted;
    if (side === null || holdMode) return;
    quietStart(side);
    // A start refused there and then keeps its reason on the status line.
    if (wanted !== null) view.status('listening');
  }

  function readOut(line: Line): void {
    const speech = deps.speech;
    const words = line.translation;
    if (!speech || !readAloud || !words) return;
    const mine = generation;
    readings += 1;
    speeches = speeches
      .then(async () => {
        if (!readAloud || mine !== generation) return;
        if (!(await speech.canSpeak(line.to))) {
          if (!toldNoVoice.has(line.to)) {
            toldNoVoice.add(line.to);
            view.noVoice(line.to);
          }
          return;
        }
        if (!readAloud || mine !== generation) return;
        hush();
        await speech.speak(words, line.to);
      })
      .catch(() => undefined)
      .finally(() => {
        readings -= 1;
        if (readings === 0) unhush();
      });
  }

  /* ── a sentence ──────────────────────────────────────────────────── */

  function begin(from: string, to: string): void {
    if (started.has(mode)) return;
    started.add(mode);
    deps.count.start(mode, from, to);
  }

  function addLine(side: Side, from: string, to: string, original: string): Line {
    const line: Line = {
      id: nextId++,
      side,
      from,
      to,
      original,
      translation: null,
      state: 'pending',
    };
    lines.push(line);
    view.line(line);
    view.hasText(true);
    return line;
  }

  function heard(side: Side, words: string): void {
    const pieces = splitSentences(words);
    if (pieces.length === 0) return;
    const { from, to } = directionOf(side, pair);
    begin(from, to);
    for (const piece of pieces) {
      const line = addLine(side, from, to, piece);
      const mine = generation;
      translations = translations.then(() => translate(line, mine)).catch(() => undefined);
    }
  }

  function settle(line: Line, mine: number, text: string | null, state: Line['state']): boolean {
    if (mine !== generation) return false;
    line.translation = text;
    line.state = state;
    view.line(line);
    return true;
  }

  function done(line: Line, mine: number, text: string, engine: Engine | null): void {
    if (!settle(line, mine, text, 'done')) return;
    if (engine) deps.count.sentence(engine, line.from, line.to);
    readOut(line);
  }

  async function viaServer(line: Line): Promise<string> {
    try {
      return await deps.server(line.original, line.from, line.to);
    } catch (err) {
      if (!(err instanceof ApiError) || err instanceof GeminiLimitError) throw err;
      const code = err.problem.code;
      if (code !== 'AT_CAPACITY' && code !== 'TIMEOUT' && code !== 'NETWORK') throw err;
      // Asked once more, soon: a sentence held back for long is no use to a conversation.
      const after = err.problem.retry_after_s;
      const wait = typeof after === 'number' && after > 0 ? after * 1000 : RETRY_WAIT_CAP_MS;
      await sleep(Math.min(wait, RETRY_WAIT_CAP_MS));
      return deps.server(line.original, line.from, line.to);
    }
  }

  async function translate(line: Line, mine: number): Promise<void> {
    if (mine !== generation) return;
    const translator = deps.device ? await deps.device.get(line.from, line.to) : null;
    if (mine !== generation) return;
    if (translator) {
      try {
        done(line, mine, await translator.translate(line.original), 'device');
        return;
      } catch {
        /* the device refused this one: the server's */
      }
    }
    if (isLimited()) {
      settle(line, mine, null, 'limited');
      return;
    }
    if (sessionRefused) {
      settle(line, mine, null, 'failed');
      return;
    }
    try {
      done(line, mine, await viaServer(line), 'gemini');
    } catch (err) {
      if (err instanceof GeminiLimitError) {
        if (settle(line, mine, null, 'limited')) meetLimit(err.limit, line.to);
        return;
      }
      if (err instanceof ApiError && err.problem.code === 'ALREADY_IN_LANGUAGE') {
        done(line, mine, line.original, null);
        return;
      }
      if (err instanceof ApiError && err.problem.state === 'E14') {
        if (settle(line, mine, null, 'failed')) noSession(err.problem);
        return;
      }
      settle(line, mine, null, 'failed');
    }
  }

  /**
   * The session couldn't be made: every sentence after this one would wait for a mint of its
   * own and fail the same way, so the microphone stops and the page's banner says why, once.
   */
  function noSession(problem: Problem): void {
    if (sessionRefused) return;
    sessionRefused = true;
    stopListening();
    // The banner says what happened; a "Stopped" line beside it would only repeat it.
    view.status(null);
    view.noSession(problem);
  }

  /* ── the limit ───────────────────────────────────────────────────── */

  function isLimited(): boolean {
    if (stillLimited(limited, clock().getTime())) return true;
    if (limited) {
      limited = null;
      view.limit(null, false);
    }
    return false;
  }

  function showLimit(target: string): void {
    if (!limited) return;
    limitedTarget = target;
    const notice = limitNotice(limited, target, recorder !== null, holdMode);
    view.limit(notice.message, notice.offerHold);
  }

  function meetLimit(limit: GeminiLimit, target: string): void {
    const fresh = !isLimited();
    limited = limit;
    if (fresh) deps.count.limit(limit.scope);
    stopListening();
    // The limit's sentence says what happened; a "Stopped" line beside it would only repeat it.
    view.status(null);
    showLimit(target);
  }

  /** A pair whose sentences would go to the server while the model can't take them. */
  function refused(from: string, to: string): boolean {
    if (!isLimited()) return false;
    return !deps.device?.likely(from, to);
  }

  /* ── Hold to talk ────────────────────────────────────────────────── */

  function onRecorderState(state: Recorder['state']): void {
    if (state === 'asking') {
      view.status('asking');
      return;
    }
    if (state === 'recording') {
      if (held === null) {
        // The first press only had the browser ask for the microphone; it has come up since.
        clipSide = null;
        recorder?.discard();
        view.status('hold-again');
        return;
      }
      view.status('holding');
    }
  }

  function clip(recording: Recording): void {
    const side = clipSide ?? 'a';
    clipSide = null;
    const { from, to } = directionOf(side, pair);
    begin(from, to);
    const line = addLine(side, from, to, '');
    view.status('translating');
    const mine = generation;
    translations = translations
      .then(async () => {
        const answer = await deps.sendClip({ recording, from, to });
        if (mine !== generation) return;
        if (!answer.ok) {
          const at = lines.indexOf(line);
          if (at >= 0) lines.splice(at, 1);
          view.drop(line);
          view.hasText(lines.length > 0);
          view.status(null);
          return;
        }
        view.status(null);
        if (answer.unavailable) {
          line.original = answer.text;
          if (settle(line, mine, null, 'limited')) meetLimit(spentForToday(clock().getTime()), to);
          return;
        }
        line.original = answer.original ?? '';
        done(line, mine, answer.text, answer.engine);
      })
      .catch(() => {
        settle(line, mine, null, 'failed');
        view.status(null);
      });
  }

  function holdStart(side: Side): void {
    if (!holdMode || !recorder || held !== null) return;
    if (!sidesOf(mode).includes(side)) return;
    const { to } = directionOf(side, pair);
    // Into English, a clip is translated without the language model; any other target needs it.
    if (to !== 'en' && isLimited()) {
      showLimit(to);
      return;
    }
    const state = recorder.state;
    // A second press while the browser still asks for the microphone holds that same start.
    if (state !== 'idle' && state !== 'stopped' && state !== 'asking') return;
    held = side;
    clipSide = side;
    view.holding(side);
    if (state !== 'asking') void recorder.start();
  }

  /** The buttons become Hold to talk: the limit's offer taken, or a recognition that never works. */
  function toHold(): void {
    if (!recorder || holdMode) return;
    stopListening();
    holdMode = true;
    view.holdMode(true);
    showLimit(limitedTarget);
  }

  function holdEnd(side: Side): void {
    if (held !== side || !recorder) return;
    held = null;
    view.holding(null);
    const state = recorder.state;
    // Still asking: once the microphone is allowed, the recording is dropped (onRecorderState).
    if (state !== 'recording' && state !== 'paused') return;
    if (recorder.seconds() < MIN_HOLD_S) {
      clipSide = null;
      recorder.discard();
      view.status('hold-short');
      return;
    }
    recorder.stop();
  }

  /* ── start ───────────────────────────────────────────────────────── */

  view.readAloud(readAloud);
  view.faceToFace(false);
  view.languages(pair);
  view.mode(mode, lines);
  view.hasText(false);
  if (!lives && !recorder) view.unsupported();
  else view.holdMode(holdMode);

  return {
    get mode() {
      return mode;
    },
    get pair() {
      return pair;
    },
    get on() {
      return wanted;
    },
    get holdMode() {
      return holdMode;
    },
    get readAloud() {
      return readAloud;
    },
    get lines() {
      return lines;
    },
    async idle() {
      for (;;) {
        const t = translations;
        const s = speeches;
        await Promise.all([t, s]);
        if (t === translations && s === speeches) return;
      }
    },
    setMode(next) {
      if (next === mode || (next !== 'speak' && next !== 'conversation')) return;
      stopListening();
      if (held || clipSide) {
        held = null;
        clipSide = null;
        recorder?.discard();
        view.holding(null);
      }
      mode = next;
      view.mode(mode, lines);
    },
    setLanguage(side, code) {
      const next = pairAfterPick(pair, side, code);
      if (next === pair) {
        view.languages(pair);
        return;
      }
      pair = next;
      view.languages(pair);
      // From the picker's change, a gesture: the device may download the new pair's pack.
      for (const s of sidesOf(mode)) {
        const d = directionOf(s, pair);
        deps.device?.prepare(d.from, d.to);
      }
      relisten();
      if (limited) showLimit(directionOf(wanted ?? 'a', pair).to);
    },
    swap() {
      pair = { a: pair.b, b: pair.a };
      view.languages(pair);
      const d = directionOf('a', pair);
      deps.device?.prepare(d.from, d.to);
      relisten();
      if (limited) showLimit(d.to);
    },
    press(side) {
      deps.speech?.prime();
      if (holdMode || !lives || !sidesOf(mode).includes(side)) return;
      const next = turnAfter(wanted, side);
      if (next === null) {
        stopListening();
        return;
      }
      const { from, to } = directionOf(next, pair);
      deps.device?.prepare(from, to);
      if (refused(from, to)) {
        showLimit(to);
        return;
      }
      // After a session that couldn't be made, a tap is the visitor trying again.
      sessionRefused = false;
      // The session is minted while the visitor starts talking, not with the first sentence,
      // unless the device translates this pair and nothing will be sent at all.
      if (!deps.device) deps.warm?.();
      else
        void deps.device.get(from, to).then((translator) => {
          if (!translator) deps.warm?.();
        });
      listen(next);
    },
    holdStart,
    holdEnd,
    useHold: toHold,
    setReadAloud(on) {
      readAloud = on && deps.speech !== null;
      view.readAloud(readAloud);
      if (readAloud) deps.speech?.prime();
      else deps.speech?.cancel();
    },
    setFaceToFace(on) {
      facing = on;
      view.faceToFace(facing);
    },
    async copy() {
      const text = sessionText(lines, deps.nameOf);
      if (!text) return false;
      const ok = await deps.copy(text);
      view.status(ok ? 'copied' : 'copy-failed');
      return ok;
    },
    download() {
      const text = sessionText(lines, deps.nameOf);
      if (!text) return false;
      // Made here, from the lines on screen.
      deps.save(new Blob([text], { type: 'text/plain;charset=utf-8' }), sessionFileName(clock()));
      deps.count.download();
      return true;
    },
    clear() {
      generation += 1;
      lines.length = 0;
      started.clear();
      deps.speech?.cancel();
      view.mode(mode, lines);
      view.hasText(false);
      for (const s of ['a', 'b'] as const) view.interim(s, '');
      if (wanted === null) view.status(null);
    },
  };
}
